import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ArrowLeft, Loader2, Compass, Lock } from "lucide-react";
import { API_BASE } from "@/lib/api";
import { JourneyPlanView } from "@/components/site/JourneyPlan";

export default function JourneyView() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [lead, setLead] = useState({ name: "", email: "" });
  const [unlocking, setUnlocking] = useState(false);

  useEffect(() => {
    axios.get(`${API_BASE}/journey/${id}`).then((r) => setData(r.data)).catch(() => setError("Journey not found"));
  }, [id]);

  const unlock = async (e) => {
    e.preventDefault();
    setUnlocking(true);
    try {
      const r = await axios.post(`${API_BASE}/journey/${id}/unlock`, lead);
      setData(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not unlock.");
    } finally {
      setUnlocking(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F5F0] text-[#2D372B] px-6 md:px-12 py-14 md:py-20">
      <div className="max-w-4xl mx-auto mb-8 flex items-center justify-between print:hidden">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-[#5C665A] hover:text-[#2D372B]"><ArrowLeft size={15} /> Coach Sanjay</Link>
        <Link to="/journey" className="text-sm text-[#89937C] hover:text-[#2D372B] inline-flex items-center gap-1.5"><Compass size={14} /> Plan my own journey</Link>
      </div>
      {error && <p data-testid="journey-view-error" className="text-center text-[#AD6A48] py-20">{error}</p>}
      {!data && !error && <div className="flex justify-center py-20"><Loader2 size={22} className="animate-spin text-[#89937C]" /></div>}
      {data?.unlocked && data.plan && <JourneyPlanView plan={data.plan} journeyId={id} />}
      {data && !data.unlocked && data.teaser && (
        <div className="max-w-xl mx-auto" data-testid="journey-view-teaser">
          <div className="overline mb-3">A shared journey</div>
          <h1 className="font-serif-display text-4xl sm:text-5xl leading-[1.05]">{data.teaser.title}</h1>
          <p className="mt-4 text-[#5C665A]">{data.teaser.summary}</p>
          <form onSubmit={unlock} className="mt-8 bg-white border border-[#E5E2D9] rounded-3xl p-7">
            <div className="flex items-center gap-2 text-sm text-[#5C665A] mb-4"><Lock size={14} className="text-[#AD6A48]" /> Enter your details to view the full itinerary</div>
            <input required minLength={2} value={lead.name} onChange={(e) => setLead({ ...lead, name: e.target.value })} placeholder="Your name" className="w-full h-12 rounded-xl bg-[#F7F5F0] border border-[#E5E2D9] px-4 mb-3 focus:outline-none focus:border-[#89937C]" />
            <input required type="email" value={lead.email} onChange={(e) => setLead({ ...lead, email: e.target.value })} placeholder="Email address" className="w-full h-12 rounded-xl bg-[#F7F5F0] border border-[#E5E2D9] px-4 mb-4 focus:outline-none focus:border-[#89937C]" />
            <button disabled={unlocking} className="w-full inline-flex items-center justify-center gap-2 rounded-full px-6 py-3.5 bg-[#2D372B] text-[#F7F5F0] font-medium hover:bg-[#89937C] transition-colors disabled:opacity-60">
              {unlocking ? <Loader2 size={15} className="animate-spin" /> : <Compass size={15} />} View full itinerary
            </button>
          </form>
        </div>
      )}
    </div>
  );
}
